import React from 'react';
import {OrderSummaryDataHook} from '../../OrderSummary';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import {DestinationPicker} from '../DestinationPicker/DestinationPicker';
import {useTranslation} from '@wix/yoshi-flow-editor';
import type {TFunction} from '@wix/wix-i18n-config';
import orderSummaryStyle from '../../OrderSummary.scss';
import {isFreeShippingCoupon} from '../../../../../../../domain/utils/coupon';
import {CouponModel} from '../../../../../../../domain/models/Cart/Coupon.model';

const ShippingDestinationAndShippingRates = () => {
  const {t} = useTranslation();
  const {shouldDisplayDeliveryError, isPickup, isPickupPoint} = useControllerProps().orderStore;
  const {cartSummary, cartModel, refactorCouponOnCart, removePickupPointSpecificTitleOnCart} =
    useControllerProps().cartStore;
  const appliedCoupon = refactorCouponOnCart
    ? cartSummary?.couponDiscount
    : cartSummary?.appliedCoupon || cartModel.coupon;

  const getShippingRowValueText = (): string => {
    if (
      cartSummary.priceSummary.delivery.convertedAmount === 0 ||
      (refactorCouponOnCart
        ? cartSummary?.couponDiscount?.isFreeShippingCoupon
        : isFreeShippingCoupon(appliedCoupon as CouponModel))
    ) {
      return t('cart.free_shipping');
    }
    return cartSummary.priceSummary.delivery.formattedConvertedAmount;
  };

  const displayPrice = !shouldDisplayDeliveryError;
  return (
    <dl className={orderSummaryStyle.line}>
      <dt>
        <div data-hook={OrderSummaryDataHook.TotalShippingShippingDestinationWithTitle}>
          <span data-hook={OrderSummaryDataHook.TotalShippingTitle}>
            {getTotalShippingTitle({t, isPickup, isPickupPoint, removePickupPointSpecificTitleOnCart})}
          </span>
          &nbsp;
          <DestinationPicker dataHook={OrderSummaryDataHook.ShippingDestinationRoot} />
        </div>
      </dt>

      {displayPrice && (
        <dd
          data-hook={OrderSummaryDataHook.TotalShippingEstimatedShipping}
          data-wix-total-shipping-estimated-shipping={OrderSummaryDataHook.TotalShippingEstimatedShipping}>
          {getShippingRowValueText()}
        </dd>
      )}
    </dl>
  );
};

export const TotalShipping = () => {
  const {isEstimatedDestinationIsShippable} = useControllerProps().orderStore;
  return (
    <div data-hook={OrderSummaryDataHook.TotalShippingRoot}>
      {isEstimatedDestinationIsShippable ? (
        <ShippingDestinationAndShippingRates />
      ) : (
        <dt>
          <DestinationPicker dataHook={OrderSummaryDataHook.ShippingDestinationRoot} />
        </dt>
      )}
    </div>
  );
};

function getTotalShippingTitle({
  t,
  isPickup,
  isPickupPoint,
  removePickupPointSpecificTitleOnCart,
}: {
  t: TFunction;
  isPickup: boolean;
  isPickupPoint: boolean;
  removePickupPointSpecificTitleOnCart: boolean;
}) {
  if (isPickupPoint && !removePickupPointSpecificTitleOnCart) {
    return t('cart.estimated_shipping');
  }
  return isPickup ? t('cart.estimated_pickup') : t('cart.estimated_shipping');
}
